import clsx from 'clsx';

const TimeoutSection = ({ maxGames, timeoutCount }: { maxGames: number; timeoutCount: number }) => (
	<div className="flex flex-row items-center gap-2">
		<div
			className={clsx('flex flex-col items-center absolute', {
				'left-[370px]': maxGames === 1,
				'left-[320px]': maxGames === 2,
				'left-[280px]': maxGames === 3,
				'left-[250px]': maxGames === 4,
				'left-[220px]': maxGames === 5
			})}
		>
			<p className="text-xs text-gray-900">1 minute per time-out</p>
		</div>

		{Array.from({ length: maxGames }, (_, gameIdx) => (
			<div key={`timeout-${gameIdx}`} className="flex flex-row items-center justify-center gap-1 w-14">
				{Array.from({ length: Math.min(timeoutCount, 3) }, (_, timeoutIdx) => (
					<div key={`timeout-box-${gameIdx}-${timeoutIdx}`} className="w-3 h-3 border border-gray-900">
						<p className="text-[8px] text-gray-900">{timeoutIdx + 1}</p>
					</div>
				))}
			</div>
		))}
	</div>
);

interface SimplifiedMatchScoreRowProps {
	firstTeamTitle: string;
	secondTeamTitle: string;
	firstTeamPlayers: string[];
	secondTeamPlayers: string[];
	maxGames: number;
	isNoSetupTeamOneLineup: boolean;
	isNoSetupTeamTwoLineup: boolean;
	timeoutCount?: number;
}

export const SimplifiedMatchScoreRow = ({
	firstTeamTitle,
	secondTeamTitle,
	firstTeamPlayers,
	secondTeamPlayers,
	maxGames,
	isNoSetupTeamOneLineup,
	isNoSetupTeamTwoLineup,
	timeoutCount = 0
}: SimplifiedMatchScoreRowProps) => {
	return (
		<div className="flex w-full flex-col">
			{/* Away Section */}
			<div className="flex w-full flex-row justify-between items-center text-center">
				<div className="flex flex-col gap-2 text-left w-80 min-w-80">
					<p className="font-bold tracking-wider text-start">{firstTeamTitle} (Away)</p>
					{isNoSetupTeamOneLineup ? <pre> {'          /'} </pre> : null}
					{!isNoSetupTeamOneLineup &&
						firstTeamPlayers.map((player, index) => (
							<p key={`${player}-${index}`} className="line-clamp-3">
								{player}
							</p>
						))}
				</div>

				<div className="flex flex-col items-center justify-between gap-2">
					<div className="flex flex-row items-center gap-10">
						{Array.from({ length: maxGames }, (_, i) => (
							<div key={`g-${i + 1}`}>{`G:${i + 1}`}</div>
						))}
					</div>
					<div className="flex flex-row items-center gap-2">
						{Array.from({ length: maxGames }, (_, i) => (
							<ScoreBox key={`t1-g-${i}`} />
						))}
					</div>
					{timeoutCount > 0 && <TimeoutSection maxGames={maxGames} timeoutCount={timeoutCount} />}
				</div>

				<div
					className={clsx('flex flex-col items-center gap-2', {
						'mb-5': timeoutCount > 0
					})}
				>
					<p>Initials</p>
					<ScoreBox />
				</div>
			</div>

			<div className="w-full border-t border-dashed border-gray-600 my-2" />

			{/* Home Section */}
			<div className="flex w-full flex-row justify-between text-center">
				<div className="flex flex-col justify-between gap-2 text-left w-80 min-w-80">
					{isNoSetupTeamTwoLineup ? <pre> {'          /'} </pre> : null}
					{!isNoSetupTeamTwoLineup &&
						secondTeamPlayers.map((player) => (
							<p key={player} className="line-clamp-3">
								{player}
							</p>
						))}
					<p className="font-bold tracking-wider text-start mb-2.5">{secondTeamTitle} (Home)</p>
				</div>

				<div className="flex flex-col items-center justify-between gap-2">
					<div className="flex flex-row items-start gap-2">
						{Array.from({ length: maxGames }, (_, i) => (
							<ScoreBox key={`t2-g-${i}`} />
						))}
					</div>
					{timeoutCount > 0 && <TimeoutSection maxGames={maxGames} timeoutCount={timeoutCount} />}
				</div>

				<div className="flex flex-row items-start gap-2">
					<ScoreBox />
				</div>
			</div>
		</div>
	);
};

const ScoreBox = () => <div className="flex h-9 w-14 items-center justify-center rounded-md border !border-gray-900 font-bold !text-gray-900" />;
